'use client';
import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import useMediaQuery from '@mui/material/useMediaQuery';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import type { ReactNode } from 'react';
import {
  speckleBackgroundUrl,
  warm,
  neutral,
  imageFullWidthXs,
  imageFullWidthSmAndUp,
} from '@style/variables.js';
import {
  BackgroundContainer,
  Container,
  SmokeImage1,
  SmokeImage2,
  SmokeWrapper,
  SplitBackgroundContainer,
} from './styles';

const BackgroundStyleEnum = Object.freeze({
  White: 'White',
  Grey: 'Grey',
  TextureLight: 'Texture Light',
  TextureDark: 'Texture Dark',
  ButcherPaper: 'Butcher Paper',
});

const SmokePlacementEnum = Object.freeze({
  Bottom: 'Bottom',
  Middle: 'Middle',
});

const getBackground = (top: string) => {
  let topBackgroundColor = 'white';
  let topBackgroundImageUrl: string | null = null;
  switch (top) {
    case BackgroundStyleEnum.TextureLight:
      topBackgroundColor = warm[50];
      topBackgroundImageUrl = speckleBackgroundUrl;
      break;
    case BackgroundStyleEnum.TextureDark:
      topBackgroundColor = neutral[900];
      topBackgroundImageUrl = speckleBackgroundUrl;
      break;
    case BackgroundStyleEnum.Grey:
      topBackgroundColor = warm[200];
      break;
    default:
      break;
  }
  return { topBackgroundColor, topBackgroundImageUrl };
};

interface Props {
  top: string;
  bottom: string;
  smokePlacement: keyof typeof SmokePlacementEnum;
  children?: ReactNode;
}

const SplitBackgroundClientBoundary = ({ top, bottom, smokePlacement, children }: Props) => {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion)', { noSsr: true });
  const sectionRef = useRef(null);
  const smokeImg1Ref = useRef(null);
  const smokeImg2Ref = useRef(null);
  const smokeWrapperRef = useRef(null);
  const smokePlacementRef = useRef(null);
  const smAndUp = useMediaQuery((theme: any) => theme.breakpoints.up('sm'));
  const { topBackgroundColor, topBackgroundImageUrl } = getBackground(top);

  const smokeImgUrl = buildImageSrc(
    {
      endpoint: 'traeger',
      name: 'transparent-smoke',
    },
    smAndUp ? imageFullWidthSmAndUp : imageFullWidthXs,
    0,
    0,
  );

  useEffect(() => {
    if (reduceMotion) return;
    // @ts-expect-error gsap
    const heroGrillTimeline = new gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        endTrigger: smokePlacementRef.current,
        start: 'top center',
        end: 'bottom center',
        scrub: 0.5,
      },
    });
    heroGrillTimeline
      .addLabel('start')
      .from(
        smokeWrapperRef.current,
        {
          yPercent: smokePlacement === SmokePlacementEnum.Middle ? 20 : 100,
          opacity: smokePlacement === SmokePlacementEnum.Middle ? 0.5 : 0,
          duration: 3,
        },
        'smoke',
      )
      .from(
        smokeImg1Ref.current,
        {
          xPercent: -12,
          duration: 3,
        },
        'smoke',
      )
      .from(
        smokeImg2Ref.current,
        {
          xPercent: 6,
          duration: 3,
        },
        'smoke',
      )
      .addLabel('end');
  }, [reduceMotion]);

  return (
    <SplitBackgroundContainer
      topBackgroundColor={topBackgroundColor}
      topBackgroundImageUrl={topBackgroundImageUrl}
    >
      <Section
        rootRef={sectionRef}
        gutterY
        white={bottom === BackgroundStyleEnum.White}
        dark={
          bottom === BackgroundStyleEnum.TextureDark || bottom === BackgroundStyleEnum.ButcherPaper
        }
        texture={
          bottom === BackgroundStyleEnum.TextureLight || bottom === BackgroundStyleEnum.TextureDark
        }
        butcherPaper={bottom === BackgroundStyleEnum.ButcherPaper}
        className="root "
      >
        {smokePlacement && (
          <BackgroundContainer
            ref={smokePlacementRef}
            middle={smokePlacement === SmokePlacementEnum.Middle}
          >
            <SmokeWrapper ref={smokeWrapperRef}>
              <SmokeImage1 ref={smokeImg1Ref}>
                <img src={smokeImgUrl} role="presentation" alt="" />
              </SmokeImage1>
              <SmokeImage2 ref={smokeImg2Ref}>
                <img src={smokeImgUrl} role="presentation" alt="" />
              </SmokeImage2>
            </SmokeWrapper>
          </BackgroundContainer>
        )}
        <Container>
          {children}
        </Container>
      </Section>
    </SplitBackgroundContainer>
  );
};

export default SplitBackgroundClientBoundary;
